import { useEffect, useMemo } from 'react'
import { toast } from 'react-toastify'

import { useSetState, useThunkDispatch } from '@domain-group/react-hooks'

import {
  deepSetObject, digObject, matchFilterBetweenDates, snakeToTitleCase, sortArrayBy,
} from '@domain-group/javascript-utils'

import formatDate from '@functions/formatDate'
import suggestProperty from '@functions/dataProvider'
import { browseComparables as browseComparablesApi } from '@functions/campaignComparable'
import { getFullAddress } from '@functions/campaign'

import useCampaign from '@hooks/useCampaign'
import useSelector from '@hooks/useSelector'

import * as campaignActions from '@redux/modules/campaign'
import type { AppDispatch } from '@redux/store'

import type {
  CampaignComparableModel, CampaignComparableSearchModel, CampaignModel, PropertySuggestionModel,
} from '@models/types'

import type { FilterModel } from '@modals/BrowseCampaignComparablesModal/utils/filterModel'

import buildFilters from '../utils/buildFilters'
import coreLogicFilters from '../utils/coreLogicFilters'
import defaultFilters from '../utils/defaultFilters'

const defaultState = {
  browseComparableType: '',
  browseResults: [] as CampaignComparableSearchModel[],
  browsedComparableType: '',
  filters: {},
  hasDirtyFilterFields: false,
  propertyMatchId: '',
  propertyMatchSuggestions: [] as PropertySuggestionModel[],
  searchingBrowseComparables: false,
  searchingForPropertyMatch: false,
  showFilters: false,
  showImages: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

type DataProviderResult = {
  data_provider: string,
  legal_description?: string,
  id: number,
}

type UpdateCampaignDataProviderParams = {
  campaign: CampaignModel,
  dispatch: AppDispatch,
  dataProviderResult: DataProviderResult,
  setState: SetState,
}

const updateCampaignDataProvider = (params: UpdateCampaignDataProviderParams) => {
  const {
    campaign, dispatch, dataProviderResult, setState,
  } = params

  setState({ searchingForPropertyMatch: true })

  const { updateCampaign } = campaignActions
  const { data, id } = campaign

  const updatedData = {
    ...data,
    data_provider: {
      title: dataProviderResult.data_provider,
      property_id: dataProviderResult.id,
    },
  }

  const updatedCampaign = {
    id,
    data: JSON.stringify(updatedData),
    legal_description: dataProviderResult.legal_description,
  }

  // @ts-expect-error TS2345
  return dispatch(updateCampaign(updatedCampaign)).then((response) => {
    setState({ searchingForPropertyMatch: false })
    return response
  })
}

type ResetCampaignDataProviderParams = {
  campaign: CampaignModel,
  dispatch: AppDispatch,
  setState: SetState,
}

const resetCampaignDataProvider = (params: ResetCampaignDataProviderParams) => {
  const { campaign, dispatch, setState } = params

  const { updateCampaign } = campaignActions
  const { data, id } = campaign

  const updatedData = {
    ...data,
    data_provider: null,
  }

  const updatedCampaign = {
    id,
    data: JSON.stringify(updatedData),
    legal_description: null,
  }

  setState({ searchingForPropertyMatch: true })

  // @ts-expect-error TS2345
  return dispatch(updateCampaign(updatedCampaign)).then((response) => {
    setState({ searchingForPropertyMatch: false })
    return response
  })
}

type FetchPropertyMatchSuggestionsParams = {
  autoSelect?: Boolean,
  campaign: CampaignModel,
  dispatch: AppDispatch,
  setState: SetState,
}

const fetchPropertyMatchSuggestions = (params: FetchPropertyMatchSuggestionsParams) => {
  const {
    autoSelect, campaign, dispatch, setState,
  } = params

  const address = getFullAddress(campaign)
  setState({ searchingForPropertyMatch: true })

  // order of these statements is important
  suggestProperty({ string: address }).then((response) => {
    // @ts-expect-error TS2339
    const { success, data } = response

    if (!success){
      setState({ searchingForPropertyMatch: false })
    }

    if (autoSelect && data.length === 1){
      updateCampaignDataProvider({
        campaign, dispatch, dataProviderResult: data[0], setState,
      })
    }

    if (data.length){
      setState({ propertyMatchSuggestions: data, searchingForPropertyMatch: false })
    }

    setState({ searchingForPropertyMatch: false })
  })
}

type BrowseComparablesParams = {
  dataProviderPropertyId: number,
  filters?: object,
  setState: SetState,
  state: Partial<typeof defaultState>,
}

const browseComparables = (params: BrowseComparablesParams) => {
  const {
    dataProviderPropertyId, filters, setState, state: { browseComparableType },
  } = params

  setState({ searchingBrowseComparables: true })

  // @ts-expect-error TS2322
  const entityOptions = buildFilters({ comparableType: browseComparableType, filters })

  // @ts-expect-error TS2322
  browseComparablesApi({ browseComparableType, dataProviderPropertyId, entityOptions }).then((response) => {
    // @ts-expect-error TS2339
    const { data, errors, success } = response

    if (!success){
      toast.warn(errors[0])

      setState({
        browseResults: [],
        browsedComparableType: browseComparableType,
        hasDirtyFilterFields: false,
        searchingBrowseComparables: false,
      })

      return
    }

    setState({
      // @ts-expect-error TS2322
      browseResults: data,
      browsedComparableType: browseComparableType,
      hasDirtyFilterFields: false,
      searchingBrowseComparables: false,
    })
  })
}

type ChangeFilterParams = {
  key: string,
  name: string,
  setState: SetState,
  state: Partial<typeof defaultState>,
  value: string,
}

const changeFilter = (params: ChangeFilterParams) => {
  const {
    key, name, value, state, setState,
  } = params

  const { filters = {} } = state

  const newFilters = deepSetObject(filters, `${key}.${name}`, value)

  setState({
    filters: newFilters,
    hasDirtyFilterFields: true,
  })
}

type BrowseCampaignComparablesModalParams = {
  campaignComparable: CampaignComparableModel,
}

const useBrowseCampaignComparablesModal = (params: BrowseCampaignComparablesModalParams) => {
  const { campaignComparable } = params

  const dispatch = useThunkDispatch()

  const [state, setState] = useSetState(defaultState)
  const {
    browseComparableType,
    browseResults,
    browsedComparableType,
    filters,
    hasDirtyFilterFields,
    propertyMatchId,
    propertyMatchSuggestions,
    searchingBrowseComparables,
    searchingForPropertyMatch,
    showFilters,
    showImages,
  } = state

  const { campaign } = useCampaign({ id: campaignComparable.campaign_id })

  const entities = useSelector(reduxState => reduxState.entities)
  const { agencies } = entities

  const agency = agencies[campaign.agency_id]
  // Sometimes set as empty string so can't use default in digObject
  const agencyDataProvider = digObject(agency, 'options.data_provider_api') || 'pricefinder'

  const dataProviderPropertyId = digObject(campaign, 'data.data_provider.property_id', '')

  const dataProviderTitle = digObject(campaign, 'data.data_provider.title', 'pricefinder')
  const dataProviderFilters = dataProviderTitle === 'core_logic' ? { ...coreLogicFilters } : { ...defaultFilters }
  const dataProviderFiltersCopy = { ...JSON.parse(JSON.stringify(dataProviderFilters)) }

  // When searching for properties with the browseComparableType of 'agency_all' or 'market_all'
  // we get results that both do and do not have 'sold_date'
  // If we then use the 'Sold Between' filter these results won't be accurate unless we filter on the frontend as well
  const filteredBrowseResults = useMemo(() => {
    const filtered = Object.values(browseResults).filter((browseResult) => {
      const { sold_date } = browseResult

      const soldDate = formatDate(sold_date, 'dd/MM/yyyy')

      const filterSoldEndDate = digObject(filters, 'sold_date.valueMax', '')
      const filterSoldStartDate = digObject(filters, 'sold_date.value', '')

      const matchSoldDate = matchFilterBetweenDates(soldDate, filterSoldStartDate, filterSoldEndDate)

      return matchSoldDate
    })

    return sortArrayBy(filtered, 'asc', 'distance')
  }, [JSON.stringify(browseResults)])

  const filteredBrowseResultsCount = filteredBrowseResults.length
  const hasFilteredBrowseResults = !!filteredBrowseResults.length
  const hasPropertyMatchSuggestions = !!propertyMatchSuggestions.length

  // Allowed to browse if there is a browseComparableType
  // and/or there are new filters added
  const disableBrowse = browseComparableType === ''
    || (browseComparableType === browsedComparableType && !hasDirtyFilterFields)

  useEffect(() => {
    setState({ filters: dataProviderFiltersCopy })
  }, [dataProviderTitle])

  const filtersArray: FilterModel[] = Object.values(filters)
  const activeFiltersCount = filtersArray.filter(
    (f: FilterModel) => (f.value && f.id !== 'sort' && f.id !== 'radius') || (f.id === 'radius' && f.value !== '5'),
  ).length

  return {
    activeFiltersCount,
    agencyDataProviderTitle: snakeToTitleCase(agencyDataProvider),
    browseComparableType,
    filteredBrowseResultsCount,
    callbacks: {
      browseComparables: () => browseComparables({
        dataProviderPropertyId, filters, setState, state,
      }),
      changeFilter: (changeFilterParams: Partial<ChangeFilterParams>) => (
        // @ts-expect-error TS2345
        changeFilter({ setState, state, ...changeFilterParams })
      ),
      fetchPropertyMatchSuggestions: ({ autoSelect }: {autoSelect?: Boolean}) => fetchPropertyMatchSuggestions({
        autoSelect, campaign, dispatch, setState,
      }),
      resetCampaignDataProvider: () => resetCampaignDataProvider({ campaign, dispatch, setState }),
      resetFilters: () => setState({ filters: dataProviderFiltersCopy, hasDirtyFilterFields: true }),
      setModalState: (values: Partial<typeof defaultState>) => setState(values),
      updateCampaignDataProvider: (dataProviderResult: DataProviderResult) => updateCampaignDataProvider({
        campaign, dispatch, dataProviderResult, setState,
      }),
    },
    campaign,
    dataProviderPropertyId,
    disableBrowse,
    filteredBrowseResults,
    filtersArray,
    hasFilteredBrowseResults,
    hasPropertyMatchSuggestions,
    isMatchedWithDataProvider: !!dataProviderPropertyId,
    propertyMatchId,
    propertyMatchSuggestions,
    searchingBrowseComparables,
    searchingForPropertyMatch,
    showFilters,
    showImages,
  }
}

export type UseBrowseCampaignComparablesModalPayload = ReturnType<typeof useBrowseCampaignComparablesModal>

export default useBrowseCampaignComparablesModal
